import * as React from 'react';
import CustomTooltip from '../CustomTooltip';
import useTable from '../hooks/useTable';
import useTooltip from '../hooks/useTooltip';
import { localForageStore } from '../../../store/localForageStore';
import { URL_TRIP_MAP } from '../../../router/constants';

interface ILinkCellProps {
  text: string | number | null;
  align?: string;
  tooltipEnabled?: boolean;
  link?: string | null;
}

function LinkCell(props: ILinkCellProps): JSX.Element {
  const { handleAnonymizedData, isAnonymizedValue } = useTable();
  const { hover, tooltipPos, textRef, handleMouseEnter, handleMouseLeave } = useTooltip();

  const alignClass = props.align || 'left';
  const value = props.text ? handleAnonymizedData(props.text) : null;

  const redirectToMapWithStart = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    const href = event.currentTarget.href;

    // if trip-map link is provided - store rides to indexedDb
    if (href.includes('ride-map')) {
      // function to extract customerId and dataid from href
      const regex = /\/ride-map\/(\d+)-(\d+)/;
      const match = href.match(regex);

      if (match) {
        const customerId = match[1];
        const dataid = match[2];

        localForageStore.setItem('rides', [`${customerId}-${dataid}`]);
        localForageStore.setItem('multi-rides', null);

        window.open(`${URL_TRIP_MAP}`, '_blank');
      }
    }
  };

  const isTextTruncated = () => {
    if (!textRef.current) return false;
    return textRef.current.offsetWidth < textRef.current.scrollWidth;
  };

  const showTooltip = (hover && (isTextTruncated() || props.tooltipEnabled)) ?? false;

  // Handle case where text is provided but link is not (anonymized data, V3BIZ-1136)
  if (props.text && props.link === null) {
    return (
      <div
        className={`text-cell align-${alignClass}`}
        style={{
          whiteSpace: 'nowrap',
          flex: 1,
        }}
      >
        {props.text && !isAnonymizedValue(props.text) && <div className="text-cell">{value}</div>}
      </div>
    );
  }

  return (
    <div
      className={`text-cell align-${alignClass}`}
      style={{
        whiteSpace: 'nowrap',
        flex: 1,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <CustomTooltip content={props.text || ''} show={showTooltip} position={tooltipPos}>
        <div className="text-cell" ref={textRef}>
          {props.text && !isAnonymizedValue(props.text) ? (
            <a
              data-cy="row-link-address"
              href={props.link || `https://google.com/maps?q=${value}`}
              onClick={redirectToMapWithStart}
              target="_blank"
              rel="noreferrer"
              className="text-cell"
            >
              {value}
            </a>
          ) : (
            value || <>&ndash;</>
          )}
        </div>
      </CustomTooltip>
    </div>
  );
}

export default LinkCell;
